import { ArrowBack, Downloading, Print } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import { generatePDF } from "../../components/order/export_pdf";
import OrderDetailsBody from "../../components/order/OrderDetailsBody";
import PaymentModal from "../../components/order/PaymentModal";
import { presentUuid } from '../../helpers/formatters';

function OrderDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [loadingPDF, setLoadingPDF] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const fetchOrderDetails = () => {
        setIsLoading(true);
        api.get(`orders/${id}`)
            .then(response => {
                if (response.data && response.data.success && response.data.data) {
                    setOrder(response.data.data);
                    fetchTransactionByOrderId(response.data.data.id);
                } else {
                    console.error('Estrutura de resposta inesperada:', response.data);
                    setDidNotLoad(true);
                }
            })
            .catch(error => {
                console.error('Erro ao buscar detalhes da venda:', error);
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchTransactionByOrderId = (orderId) => {
        if (!orderId) return;

        api.get(`orders/${orderId}/transaction`)
            .then(response => {
                const tx = response.data?.data?.transaction;
                if (response.data?.success && tx && (tx.id || tx.net_amount != null)) {
                    setTransaction(tx);
                }
            })
            .catch(error => {
                console.error('Erro ao buscar transação relacionada:', error);
            });
    };

    const handlePrintReceipt = (orderData) => {
        setLoadingPDF(true);
        generatePDF(orderData);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000);
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    if (!order) {
        return <Loading />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/orders')} to={'/admin/orders'}>
                    Pedidos
                </Link>
                <Typography color="text.primary">Detalhes do pedido</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                <Typography variant="h4" gutterBottom>
                    Detalhes do pedido #{order.id ? presentUuid(order.id) : 'Carregando...'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/admin/orders')}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate(`/admin/orders/${order.id}/edit`)}
                    >
                        Editar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loadingPDF ? <Downloading /> : <Print />}
                        onClick={() => handlePrintReceipt(order)}
                        disabled={loadingPDF}
                    >
                        {loadingPDF ? 'Gerando...' : 'Imprimir Comprovante'}
                    </Button>
                </Box>
            </Box>

            <OrderDetailsBody
                order={order}
                transaction={transaction}
                onOrderUpdated={setOrder}
            />

            <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Ações
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {!order.payment_details?.paid && order.id && (
                                    <PaymentModal
                                        id={order.id}
                                        onSuccess={fetchOrderDetails}
                                    />
                                )}
                                <Alert severity="info">
                                    Use o botão "Imprimir Comprovante" no topo da página para gerar o PDF da venda.
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
}

export default OrderDetailsPage;
